import { FormGroup, FormSection, Radio } from '@ksyos/design-system';
import type { IOorkV1ProcedureCustomControlData } from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1ProcedureCustomControlData';
import {
    GestoordeDonkerAdaptie,
    RefractieOptimaleCorrectie,
} from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1ProcedureCustomControlData';
import { getValuesFromEnum } from '@common/utils/getValuesFromEnum';
import { RadioGroupFormGroupField } from '@frontend/components/fields/RadioGroupField';
import { TextAreaFormGroupField } from '@frontend/components/fields/TextAreaField';
import { getSelectedLanguage } from '@frontend/translate';
import type { IOorkV1ProcedureProps } from './types';
import { DateInput } from '../../../../elements/common';
import { BooleanRadioGroupFormGroupField } from '../../../../fields/BooleanRadioGroupField';
import { CheckboxGroupOld } from '../../../../fields/CheckBoxGroup';
import { OorkV1ProcedureVisusFieldSetWrapper } from './OorkV1ProcedureVisusFieldSetWrapper';
import { defaultBooleanRadioGroupFormGroupFieldProps, defaultTextAreaFormGroupFieldProps } from './types';

export function OorkV1ProcedureExamination(props: Readonly<IOorkV1ProcedureProps>) {
    const translate = getSelectedLanguage();

    return (
        <FormSection title={translate.diagnosticReport.examination}>
            <FormGroup
                hasErrors={props.errors.has('customControlData.datum_onderzoek')}
                isRequired={true}
                label="Datum van het onderzoek"
            >
                <DateInput
                    error={props.errors.get('customControlData.datum_onderzoek')}
                    hasMaxWidth={true}
                    id="datum_onderzoek"
                    onChange={async (value) => {
                        await props.handleChangeCustomControl({ datum_onderzoek: value });
                        await props.validateOneCustomControl('datum_onderzoek');
                    }}
                    placeholder={translate.common.dateFormat}
                    value={props.customControlData.datum_onderzoek}
                    variant="outline"
                />
            </FormGroup>

            <CheckboxGroupOld
                checkboxOptions={[
                    {
                        checked: props.customControlData.eigen_correctie.bril,
                        label: 'Bril',
                        onChange: async (value) => await onChangeEigenCorrectie('bril', value),
                    },
                    {
                        checked: props.customControlData.eigen_correctie.contactlenzen_overdag,
                        label: 'Contactlenzen overdag',
                        onChange: async (value) => await onChangeEigenCorrectie('contactlenzen_overdag', value),
                    },
                    {
                        checked: props.customControlData.eigen_correctie.nachtlenzen,
                        label: 'Nachtlenzen',
                        onChange: async (value) => await onChangeEigenCorrectie('nachtlenzen', value),
                    },
                    {
                        checked: props.customControlData.eigen_correctie.geen_eigen_correctie,
                        label: 'Geen eigen correctie',
                        onChange: async (value) => await onChangeEigenCorrectie('geen_eigen_correctie', value),
                    },
                ]}
                error={props.errors.get('customControlData.eigen_correctie')}
                hasErrors={props.errors.has('customControlData.eigen_correctie')}
                isRequired={true}
                label="Eigen correctie"
            />
            <OorkV1ProcedureVisusFieldSetWrapper
                customControlData={props.customControlData}
                errors={props.errors}
                handleChangeCustomControl={props.handleChangeCustomControl}
                isReadOnly={false}
                validateOneCustomControl={props.validateOneCustomControl}
            />

            <RadioGroupFormGroupField
                fieldKey="refractie_optimale_correctie"
                isRequired={true}
                label="Refractie bij optimale correctie"
            >
                <Radio
                    label="< 8D"
                    value={RefractieOptimaleCorrectie.kleinerDan8D}
                />
                <Radio
                    label="≥ 8D (alleen kiezen indien beide ogen)"
                    value={RefractieOptimaleCorrectie.groterDanGelijkAan8D}
                />
            </RadioGroupFormGroupField>
            <BooleanRadioGroupFormGroupField
                {...defaultBooleanRadioGroupFormGroupFieldProps}
                fieldKey="aanwijzingen_defecten_gezichtsveld"
                isRequired={true}
                label="Aanwijzingen voor defecten in het gezichtsveld aanwezig, volgens Donders"
                onChange={async (value) => {
                    if (
                        !props.customControlData.aanwijzingen_defecten_gezichtsveld &&
                        props.customControlData.beschrijving_gezichtsvelddefecten
                    ) {
                        await props.handleChangeCustomControl({ beschrijving_gezichtsvelddefecten: null });
                    }
                    await props.handleChangeCustomControl({ aanwijzingen_defecten_gezichtsveld: value });
                }}
            />

            {props.customControlData.aanwijzingen_defecten_gezichtsveld && (
                <TextAreaFormGroupField
                    {...defaultTextAreaFormGroupFieldProps}
                    fieldKey="beschrijving_gezichtsvelddefecten"
                    label="Beschrijf de gezichtsvelddefecten"
                    placeholder="Een toelichting, opmerkingen of vragen..."
                />
            )}

            <BooleanRadioGroupFormGroupField
                {...defaultBooleanRadioGroupFormGroupFieldProps}
                fieldKey="sprake_van_diplopie"
                isRequired={true}
                label="Sprake van diplopie"
                onChange={async (value) => await handleChangeDiplopie(value)}
            />

            {props.customControlData.sprake_van_diplopie && (
                <>
                    <BooleanRadioGroupFormGroupField
                        {...defaultBooleanRadioGroupFormGroupFieldProps}
                        fieldKey="betrokkene_gebruikt_hulpmiddel"
                        isRequired={true}
                        label="Betrokkene gebruikt hulpmiddel"
                    />
                    <BooleanRadioGroupFormGroupField
                        {...defaultBooleanRadioGroupFormGroupFieldProps}
                        fieldKey="diplopie_is_hinderlijk"
                        isRequired={true}
                        label="Diplopie is hinderlijk"
                    />
                    <TextAreaFormGroupField
                        {...defaultTextAreaFormGroupFieldProps}
                        fieldKey="toelichting_diplopie"
                        label="Toelichting diplopie"
                        placeholder="Toelichting op diplopie..."
                    />
                </>
            )}

            <RadioGroupFormGroupField
                fieldKey="gestoorde_donkeradaptie"
                isRequired={true}
                label="Gestoorde donkeradaptie"
                onChange={async (value) => {
                    if (
                        props.customControlData.gestoorde_donkeradaptie === 'Nee' &&
                        props.customControlData.toelichting_donkeradaptie
                    ) {
                        await props.handleChangeCustomControl({ toelichting_donkeradaptie: null });
                    }
                    await props.handleChangeCustomControl({ gestoorde_donkeradaptie: value });
                }}
            >
                {getValuesFromEnum(GestoordeDonkerAdaptie).map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroupField>

            {props.customControlData.gestoorde_donkeradaptie &&
                props.customControlData.gestoorde_donkeradaptie !== 'Nee' && (
                    <TextAreaFormGroupField
                        {...defaultTextAreaFormGroupFieldProps}
                        fieldKey="toelichting_donkeradaptie"
                        label="Toelichting donkeradaptie"
                        placeholder="Toelichting op donkeradaptie..."
                    />
                )}
        </FormSection>
    );

    async function onChangeEigenCorrectie<K extends keyof IOorkV1ProcedureCustomControlData['eigen_correctie']>(
        name: K,
        value: IOorkV1ProcedureCustomControlData['eigen_correctie'][K]
    ) {
        await props.handleChangeCustomControl({
            eigen_correctie: {
                ...props.customControlData.eigen_correctie,
                [name]: value,
            },
        });
        await props.validateOneCustomControl('eigen_correctie');
    }

    async function handleChangeDiplopie(value: boolean | null) {
        await props.handleChangeCustomControl({
            betrokkene_gebruikt_hulpmiddel: null,
            diplopie_is_hinderlijk: null,
            sprake_van_diplopie: value,
            toelichting_diplopie: null,
        });
    }
}
